import {
  blackScholesPriceMany,
  OptionBatchColumns,
} from '@insiderfinance/totalfinance/options/batch';
import { calculateReturnOnCost } from 'utils/shared/quantlib/returnOnCost';
import {
  buildSpotGrid,
  CurveResult,
  StrategyKernel,
  StrategyPoint,
} from 'utils/shared/quantlib/strategyKernel';
import {
  expiryDelta,
  INTRINSIC_LIMIT_TIME_YEARS,
  intrinsicOptionValue,
  OPTIONS_PER_CONTRACT,
  YEAR_IN_MS,
} from 'utils/shared/quantlib/utils';

/** Compatibility belongs to the app, not to the general-purpose library's defaults. */
export const OPC_PRICING_CONVENTIONS = Object.freeze({
  model: 'black-scholes-merton',
  exerciseStyle: 'european-proxy',
  yearDays: 365.25,
  dividendYield: 0,
  optionMultiplier: OPTIONS_PER_CONTRACT,
  expiryTimeZone: 'America/New_York',
  dateOnlyExpiryTime: '16:00',
  intrinsicCutoffYears: INTRINSIC_LIMIT_TIME_YEARS,
  entryPremiumUnits: 'per-share',
  profitUnits: 'position-dollars',
  thetaUnits: 'dollars-per-calendar-day',
  vegaRhoUnits: 'dollars-per-percentage-point',
} as const);

export interface OpcPointRequest {
  spot: number;
  asOf: number;
}

export interface OpcCurveRequest {
  from: number;
  to: number;
  /** Number of intervals, matching the existing app; produces steps + 1 base points. */
  steps: number;
  asOf: number;
  injectStrikes?: boolean;
}

export interface OpcTableRequest {
  prices: readonly number[];
  times: readonly number[];
}

export interface PreparedOpcPricing {
  readonly conventions: typeof OPC_PRICING_CONVENTIONS;
  value(input: OpcPointRequest): StrategyPoint;
  curve(input: OpcCurveRequest): CurveResult;
  /** Price outer, evaluation time inner, matching the app's payoff table. */
  table(input: OpcTableRequest): StrategyPoint[][];
}

export class OpcPricingInputError extends Error {
  constructor(public readonly field: string, message: string) {
    super(`OPC pricing: ${field} ${message}`);
    this.name = 'OpcPricingInputError';
  }
}

function finite(value: number, field: string): void {
  if (!Number.isFinite(value)) throw new OpcPricingInputError(field, 'must be finite.');
}

function nonnegative(value: number, field: string): void {
  finite(value, field);
  if (value < 0) throw new OpcPricingInputError(field, 'must be nonnegative.');
}

const GREEKS = ['delta', 'gamma', 'theta', 'vega', 'rho'] as const;
const MAX_BATCH_ROWS = 100_000;

/**
 * Freeze a selected-leg snapshot once. No chain selection, IV inversion, quote-side substitution,
 * or fabricated timestamps occur here. The kernel already contains the app's signed contracts,
 * per-leg volatility/rate, actual expiration instants and entry premiums.
 */
export function snapshotOpcPricingKernel({ kernel }: { kernel: StrategyKernel }): StrategyKernel {
  if (
    !kernel ||
    typeof kernel !== 'object' ||
    !Array.isArray(kernel.legs) ||
    !Array.isArray(kernel.stocks)
  ) {
    throw new OpcPricingInputError('kernel', 'must contain leg and stock arrays.');
  }
  if (kernel.legs.length + kernel.stocks.length > MAX_BATCH_ROWS) {
    throw new OpcPricingInputError('kernel', 'contains too many legs.');
  }
  const snapshot: StrategyKernel = {
    legs: Array.from(kernel.legs, (leg, index) => {
      if (!leg || typeof leg !== 'object')
        throw new OpcPricingInputError(`legs[${index}]`, 'must be an option leg.');
      nonnegative(leg.entryPremium, `legs[${index}].entryPremium`);
      nonnegative(leg.volatility, `legs[${index}].volatility`);
      finite(leg.strike, `legs[${index}].strike`);
      if (leg.strike <= 0)
        throw new OpcPricingInputError(`legs[${index}].strike`, 'must be positive.');
      finite(leg.riskFreeRate, `legs[${index}].riskFreeRate`);
      finite(leg.expiryMs, `legs[${index}].expiryMs`);
      finite(leg.signedContracts, `legs[${index}].signedContracts`);
      if (!Number.isSafeInteger(leg.signedContracts)) {
        throw new OpcPricingInputError(`legs[${index}].signedContracts`, 'must be a safe integer.');
      }
      if (typeof leg.isCall !== 'boolean') {
        throw new OpcPricingInputError(`legs[${index}].isCall`, 'must be a boolean.');
      }
      return Object.freeze({ ...leg });
    }),
    stocks: Array.from(kernel.stocks, (stock, index) => {
      if (!stock || typeof stock !== 'object')
        throw new OpcPricingInputError(`stocks[${index}]`, 'must be a stock leg.');
      nonnegative(stock.price, `stocks[${index}].price`);
      finite(stock.quantity, `stocks[${index}].quantity`);
      return Object.freeze({ ...stock });
    }),
    originalContractValue: kernel.originalContractValue,
    originalStockPositionValue: kernel.originalStockPositionValue,
  };
  const contractBasis = snapshot.legs.reduce(
    (total, leg) => total + leg.entryPremium * leg.signedContracts,
    0,
  );
  const stockBasis = snapshot.stocks.reduce(
    (total, stock) => total + stock.price * stock.quantity,
    0,
  );
  finite(contractBasis, 'option cost basis');
  finite(stockBasis, 'stock cost basis');
  // A stale cached basis would otherwise change P&L while still producing a plausible mark.
  if (
    contractBasis !== snapshot.originalContractValue ||
    stockBasis !== snapshot.originalStockPositionValue
  ) {
    throw new OpcPricingInputError('kernel', 'has a cost basis inconsistent with its legs.');
  }
  const costBasis = Math.abs(contractBasis * OPTIONS_PER_CONTRACT + stockBasis);
  finite(costBasis, 'cost basis');
  return snapshot;
}

function validateLiveVolatility(kernel: StrategyKernel, asOf: number): void {
  kernel.legs.forEach((leg, index) => {
    if (
      leg.signedContracts !== 0 &&
      (leg.expiryMs - asOf) / YEAR_IN_MS > INTRINSIC_LIMIT_TIME_YEARS &&
      !(leg.volatility > 0)
    ) {
      throw new OpcPricingInputError(
        `legs[${index}].volatility`,
        'must be positive for an unexpired option.',
      );
    }
  });
}

export function validateOpcPointRequest(input: OpcPointRequest, kernel: StrategyKernel): void {
  if (!input || typeof input !== 'object')
    throw new OpcPricingInputError('input', 'must be a point request.');
  nonnegative(input.spot, 'spot');
  finite(input.asOf, 'asOf');
  validateLiveVolatility(kernel, input.asOf);
}

export function validateOpcCurveRequest(input: OpcCurveRequest, kernel: StrategyKernel): void {
  if (!input || typeof input !== 'object')
    throw new OpcPricingInputError('input', 'must be a curve request.');
  nonnegative(input.from, 'from');
  nonnegative(input.to, 'to');
  finite(input.asOf, 'asOf');
  if (input.to <= input.from) throw new OpcPricingInputError('to', 'must exceed from.');
  if (!Number.isSafeInteger(input.steps) || input.steps < 1 || input.steps > 10_000) {
    throw new OpcPricingInputError('steps', 'must be a safe integer from 1 to 10000.');
  }
  if (input.injectStrikes !== undefined && typeof input.injectStrikes !== 'boolean') {
    throw new OpcPricingInputError('injectStrikes', 'must be a boolean.');
  }
  // Bound before either engine builds or allocates a grid, including strike injection.
  const pointBound = input.steps + 1 + (input.injectStrikes === false ? 0 : kernel.legs.length);
  if (pointBound * Math.max(1, kernel.legs.length + kernel.stocks.length) > MAX_BATCH_ROWS) {
    throw new OpcPricingInputError(
      'grid',
      `must not exceed ${MAX_BATCH_ROWS} leg-points per curve.`,
    );
  }
  validateLiveVolatility(kernel, input.asOf);
}

export function validateOpcTableRequest(input: OpcTableRequest, kernel: StrategyKernel): void {
  if (!input || !Array.isArray(input.prices) || !Array.isArray(input.times)) {
    throw new OpcPricingInputError('input', 'must contain prices and times arrays.');
  }
  if (
    input.prices.length > MAX_BATCH_ROWS ||
    input.times.length > MAX_BATCH_ROWS ||
    input.prices.length *
      input.times.length *
      Math.max(1, kernel.legs.length + kernel.stocks.length) >
      MAX_BATCH_ROWS
  ) {
    throw new OpcPricingInputError(
      'grid',
      `must not exceed ${MAX_BATCH_ROWS} leg-points per table.`,
    );
  }
  for (let index = 0; index < input.prices.length; index++)
    nonnegative(input.prices[index], `prices[${index}]`);
  let earliestTime = Infinity;
  for (let index = 0; index < input.times.length; index++) {
    finite(input.times[index], `times[${index}]`);
    earliestTime = Math.min(earliestTime, input.times[index]);
  }
  // Earliest is the strictest horizon: a leg live at any later evaluation is also live here.
  // Avoid a times × legs validation loop, especially when prices is empty and no rows are priced.
  if (input.times.length) validateLiveVolatility(kernel, earliestTime);
}

export function prepareTotalFinanceOpcPricing({
  kernel,
}: {
  kernel: StrategyKernel;
}): PreparedOpcPricing {
  const snapshot = snapshotOpcPricingKernel({ kernel });
  const contractBasis = snapshot.originalContractValue;
  const costBasis = Math.abs(
    contractBasis * OPTIONS_PER_CONTRACT + snapshot.originalStockPositionValue,
  );

  const evaluate = (inputs: readonly OpcPointRequest[]): StrategyPoint[] => {
    if (inputs.length * Math.max(1, snapshot.legs.length) > MAX_BATCH_ROWS) {
      throw new OpcPricingInputError(
        'grid',
        `must not exceed ${MAX_BATCH_ROWS} leg-points per batch.`,
      );
    }
    const live: Array<{ point: number; leg: number; time: number }> = [];
    const points = inputs.map(({ spot, asOf }, pointIndex) => {
      nonnegative(spot, `points[${pointIndex}].spot`);
      finite(asOf, `points[${pointIndex}].asOf`);
      let contractValue = 0;
      let delta = 0;
      let theta = 0;
      let rho = 0;
      snapshot.legs.forEach((leg, legIndex) => {
        if (leg.signedContracts === 0) return;
        const time = (leg.expiryMs - asOf) / YEAR_IN_MS;
        const scale = leg.signedContracts * OPTIONS_PER_CONTRACT;
        if (time <= INTRINSIC_LIMIT_TIME_YEARS) {
          contractValue += intrinsicOptionValue(spot, leg.strike, leg.isCall) * leg.signedContracts;
          delta += expiryDelta(spot, leg.strike, leg.isCall) * scale;
        } else if (spot === 0) {
          // Exact S→0 no-dividend BSM limit; batch pricing correctly requires strictly positive S.
          if (!leg.isCall) {
            const discountedStrike = leg.strike * Math.exp(-leg.riskFreeRate * time);
            contractValue += discountedStrike * leg.signedContracts;
            delta -= scale;
            theta += (leg.riskFreeRate * discountedStrike * scale) / 365;
            rho -= (discountedStrike * time * scale) / 100;
          }
        } else {
          if (!(leg.volatility > 0)) {
            throw new OpcPricingInputError(
              `legs[${legIndex}].volatility`,
              'must be positive for an unexpired option.',
            );
          }
          live.push({ point: pointIndex, leg: legIndex, time });
        }
      });
      for (const stock of snapshot.stocks) delta += stock.quantity;
      return {
        stockPrice: spot,
        contractValue,
        profit: 0,
        profitPerContract: 0,
        ...calculateReturnOnCost({ profit: 0, netEntryCost: costBasis }),
        delta,
        gamma: 0,
        theta,
        vega: 0,
        rho,
      };
    });
    if (live.length > 0) {
      const columns: OptionBatchColumns = {
        spot: new Float64Array(live.length),
        strike: new Float64Array(live.length),
        volatility: new Float64Array(live.length),
        riskFreeRate: new Float64Array(live.length),
        timeToExpiryYears: new Float64Array(live.length),
        type: new Int8Array(live.length),
      };
      live.forEach(({ point, leg: legIndex, time }, index) => {
        const leg = snapshot.legs[legIndex];
        columns.spot[index] = inputs[point].spot;
        columns.strike[index] = leg.strike;
        columns.volatility[index] = leg.volatility;
        columns.riskFreeRate[index] = leg.riskFreeRate;
        columns.timeToExpiryYears[index] = time;
        columns.type[index] = leg.isCall ? 1 : -1;
      });
      const result = blackScholesPriceMany(columns, { greeks: true });
      live.forEach(({ point, leg }, index) => {
        const quantity = snapshot.legs[leg].signedContracts;
        points[point].contractValue += result.price[index] * quantity;
        for (const greek of GREEKS)
          points[point][greek] += result[greek]![index] * quantity * OPTIONS_PER_CONTRACT;
      });
    }
    for (const point of points) {
      point.profitPerContract = point.contractValue - contractBasis;
      point.profit =
        point.profitPerContract * OPTIONS_PER_CONTRACT +
        snapshot.stocks.reduce(
          (total, stock) => total + stock.quantity * (point.stockPrice - stock.price),
          0,
        );
      Object.assign(
        point,
        calculateReturnOnCost({ profit: point.profit, netEntryCost: costBasis }),
      );
      for (const key of ['contractValue', 'profitPerContract', 'profit', ...GREEKS] as const) {
        finite(point[key], `result.${key}`);
      }
      if (point.profitPercent !== null) finite(point.profitPercent, 'result.profitPercent');
    }
    return points;
  };
  const value = (input: OpcPointRequest): StrategyPoint => {
    validateOpcPointRequest(input, snapshot);
    return evaluate([input])[0];
  };

  return Object.freeze({
    conventions: OPC_PRICING_CONVENTIONS,
    value,
    curve: (input: OpcCurveRequest): CurveResult => {
      validateOpcCurveRequest(input, snapshot);
      const { from, to, steps, asOf, injectStrikes = true } = input;
      const prices = buildSpotGrid(snapshot, from, to, steps, injectStrikes);
      const payoff = evaluate(prices.map((spot) => ({ spot, asOf })));
      const breakEvens: number[] = [];
      for (let index = 0; index < payoff.length; index++) {
        const point = payoff[index];
        if (Number(point.profit.toFixed(2)) === 0) {
          point.isZeroCrossing = true;
          breakEvens.push(Number(point.stockPrice.toFixed(2)));
        } else if (
          index > 0 &&
          Number(payoff[index - 1].profit.toFixed(2)) !== 0 &&
          Math.sign(point.profit) !== Math.sign(payoff[index - 1].profit)
        ) {
          let lower = Math.max(0, payoff[index - 1].stockPrice - 0.03);
          let upper = point.stockPrice + 0.03;
          let middle = (lower + upper) / 2;
          let lowerProfit = value({ spot: lower, asOf }).profit;
          for (let iteration = 0; iteration < 60 && upper - lower > 0.001; iteration++) {
            middle = (lower + upper) / 2;
            const profit = value({ spot: middle, asOf }).profit;
            if (Math.abs(profit) <= 0.001) break;
            if (Math.sign(profit) === Math.sign(lowerProfit)) {
              lower = middle;
              lowerProfit = profit;
            } else upper = middle;
          }
          breakEvens.push(Number(middle.toFixed(2)));
        }
      }
      return { payoff, breakEvens: breakEvens.sort((a, b) => a - b) };
    },
    table: (input: OpcTableRequest): StrategyPoint[][] => {
      validateOpcTableRequest(input, snapshot);
      const { prices, times } = input;
      // Price outer / time inner. Chunk by rows to bound memory without changing the economics.
      return prices.map((spot) => evaluate(times.map((asOf) => ({ spot, asOf }))));
    },
  });
}
